import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IBillingState, IBillingDetailResponse, IApplicationState, AppActionCreators, BillingActionCreators, ISaveBillingDetails, CenterActionCreators, IAppState } from '@nkr/store/index';
import { IBaseResponse } from '@nkr/models/index';
import { BillableNephrectomyStatus, ChargeStatus } from '@nkr/enums/index';
import { Panel, Alert, Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Utility } from '@nkr/services/utility';
import { IFile } from './file';
import Medicare from './insurance/medicare';
import Medicaid from './insurance/medicaid';
import Global from './insurance/global';
import NonGlobal from './insurance/non-global';
import StreamlinedBillingService from './insurance/streamlined-billing-service';
import MedicareParticipating from './medicare-participating';
import { FileType } from './file-type';
import DirectDepositInformation from '../direct-deposit-information';
import SwapInfo from '../swap-info';
import NavDonorCenter from '../nav-donor-center';
import { NavSubWrapper } from '../../styles';

interface IOwnProps {
    donorId: number
    centerId: number,
    centerAlias: string,
    tabText: string,
    onLoaded: (tabText: string) => void
}

interface IStateProps {
    billing: IBillingState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDetails: (donorId: number, centerId: number) => Promise<IBillingDetailResponse>,
    saveDetails: (details: ISaveBillingDetails) => Promise<IBillingDetailResponse>,
    saveMedicareParticipating: (centerId: number, medicareParticipating: number) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    physicianCharges: string,
    validPhysicianCharges: boolean,
    validPhysicianChargesFile: boolean,
    anesthesiaCharges: string,
    validAnesthesiaCharges: boolean,
    validAnesthesiaChargesFile: boolean,
    hospitalCharges: string,
    validHospitalCharges: boolean,
    validHospitalChargesFile: boolean,
    centerRoutingNumber: string,
    centerAccountNumber: string,
    hospitalFiles: IFile[],
    asaUnitCount: string,
    validAsaUnitCount: boolean,
    alert: {
        show: boolean,
        success: boolean,
        message: string | React.ReactNode
    },
    showMedicareParticipating: boolean,
    showSubmitAcceptConfirm: boolean,
    dirty: boolean
}

class ViewEdit extends React.Component<TProps, IState> {
    readonly state: IState = {
        physicianCharges: '',
        validPhysicianCharges: true,
        validPhysicianChargesFile: true,
        anesthesiaCharges: '',
        validAnesthesiaCharges: true,
        validAnesthesiaChargesFile: true,
        hospitalCharges: '',
        validHospitalCharges: true,
        validHospitalChargesFile: true,
        centerRoutingNumber: '',
        centerAccountNumber: '',
        hospitalFiles: [],
        asaUnitCount: '0',
        validAsaUnitCount: true,
        alert: {
            show: false,
            success: true,
            message: ''
        },
        showMedicareParticipating: false,
        showSubmitAcceptConfirm: false,
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { donorId, centerId } = this.props;

        this.props.showLoading('Getting billing details, one moment please...').then(() => {
            this.props.getDetails(donorId, centerId).then((response: IBillingDetailResponse) => {
                this.props.hideLoading();

                if (response.data.success) {
                    let { physicianCharges, physicianChargesFile, anesthesiaCharges, anesthesiaChargesFile, hospitalCharges, hospitalChargesFile, centerRoutingNumber, centerAccountNumber, isBillSent, asaUnits } = response.data.results;

                    let hospitalFiles: IFile[] = [];

                    if (physicianChargesFile)
                        hospitalFiles.push({ type: FileType.PhysicianCharges, file: null });

                    if (anesthesiaChargesFile)
                        hospitalFiles.push({ type: FileType.AnesthesiaCharges, file: null });

                    if (hospitalChargesFile)
                        hospitalFiles.push({ type: FileType.HospitalCharges, file: null });

                    this.setState({
                        physicianCharges: physicianCharges && physicianCharges > 0 ? physicianCharges.toString() : '',
                        validPhysicianCharges: physicianCharges && physicianCharges > 0,
                        anesthesiaCharges: anesthesiaCharges > 0 ? anesthesiaCharges.toString() : '',
                        validAnesthesiaCharges: anesthesiaCharges && anesthesiaCharges > 0,
                        hospitalCharges: hospitalCharges > 0 ? hospitalCharges.toString() : '',
                        validHospitalCharges: hospitalCharges && hospitalCharges > 0,
                        centerRoutingNumber,
                        centerAccountNumber,
                        asaUnitCount: asaUnits > 0 ? asaUnits.toString() : this.state.asaUnitCount,
                        hospitalFiles,
                        alert: {
                            show: isBillSent,
                            success: true,
                            message: 'This invoice has been sent, nothing more to do.'
                        }
                    }, () => {
                        let { statusId, chainId, clusterNumber, positionNumber } = this.props.billing.details.billableNephrectomy;
                        let tabText = 'Start Invoice';

                        switch (statusId) {
                            case BillableNephrectomyStatus.Unknown:
                                tabText = `Start Invoice for Swap ${chainId}/${clusterNumber}/${positionNumber}`;
                                break;

                            case BillableNephrectomyStatus.DisputedByRecipientCenter:
                                tabText = `Modify Invoice for Swap ${chainId}/${clusterNumber}/${positionNumber}`;
                                break;

                            case BillableNephrectomyStatus.AcceptedByRecipientCenter:
                            case BillableNephrectomyStatus.SentToRecipientCenter:
                            case BillableNephrectomyStatus.PaymentSentToDonorCenter:
                                tabText = `View Invoice for Swap ${chainId}/${clusterNumber}/${positionNumber}`;
                                break;
                        }

                        this.props.onLoaded(tabText);
                    });
                }
            });
        });
    }

    handleHospitalChargesChange = (charges: string) => {
        this.setState({
            hospitalCharges: charges,
            validHospitalCharges: Utility.isNumber(charges) && parseFloat(charges) >= 0
        });
    }

    handleHospitalChargesFileChange = (file: File, type: FileType) => {
        let files = _.cloneDeep(this.state.hospitalFiles);

        files = files.filter(x => x.type != type);

        let f: IFile = {
            type,
            file
        };

        files.push(f);

        this.setState({
            hospitalFiles: files
        });
    }

    handleHospitalW9Form = (file: File) => {
        let files = _.cloneDeep(this.state.hospitalFiles);

        let f: IFile = {
            type: FileType.HospitalW9Form,
            file
        };

        files.push(f);

        this.setState({
            hospitalFiles: files
        });
    }

    handleAsaUnitCountChange = (asaUnitCount: string) => {
        this.setState({
            asaUnitCount,
            validAsaUnitCount: Utility.isNumber(asaUnitCount) && parseFloat(asaUnitCount) >= 0
        });
    }

    handleCenterRoutingNumberChange = (value: string) => {
        this.setState({
            centerRoutingNumber: value
        });
    }

    handleCenterAccountNumberChange = (value: string) => {
        this.setState({
            centerAccountNumber: value
        });
    }

    handleEditMedicareParticipating = () => {
        this.setState({
            showMedicareParticipating: true
        });
    }

    handleSaveMedicareParticipating = (medicareParticipating: number) => {
        this.setState({
            showMedicareParticipating: false
        }, () => {
            this.props.showLoading('Saving medicare participating amount, one moment please...').then(() => {
                this.props.saveMedicareParticipating(this.props.centerId, medicareParticipating).then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Medicare participating amount saved successfully');
                    } else {
                        toast.error('Unable to save the Medicare participating amount');
                    }
                });
            });
        });
    }

    handleCancelMedicareParticipating = () => {
        this.setState({
            showMedicareParticipating: false
        });
    }

    handleSave = () => {
        this.save(false);
    }

    handleCancel = () => {
        window.location.href = '/center/' + this.props.centerId + '/billing/pending';
    }

    handleSubmitAccept = () => {
        this.setState({
            showSubmitAcceptConfirm: true
        });
    }

    handleSubmitAcceptYes = () => {
        this.setState({
            showSubmitAcceptConfirm: false
        }, () => {
            this.save(true);
        });
    }

    handleSubmitAcceptNo = () => {
        this.setState({
            showSubmitAcceptConfirm: false
        });
    }

    validate = (): {
        validHospitalCharges: boolean,
        validHospitalChargesFile: boolean,
        validAnesthesiaCharges: boolean,
        validAnesthesiaChargesFile: boolean,
        validPhysicianCharges: boolean,
        validPhysicianChargesFile: boolean,
        validAsaUnitCount: boolean,
        message: React.ReactNode
    } => {
        let { isSbs, isMedicare, isMedicaid, isGlobal } = this.props.billing.details;
        let { hospitalCharges, hospitalFiles, asaUnitCount } = this.state;

        let hospitalChargesFile = hospitalFiles.filter(x => x.type == 'hospital-charges');
        let anesthesiaChargesFile = hospitalFiles.filter(x => x.type == 'anesthesia-charges');

        let messages: string[] = [];

        let results = {
            validHospitalCharges: true,
            validHospitalChargesFile: true,
            validAnesthesiaCharges: true,
            validAnesthesiaChargesFile: true,
            validPhysicianCharges: true,
            validPhysicianChargesFile: true,
            validAsaUnitCount: true,
            message: null
        };

        if (isSbs) {
            // nothing to do
        }

        if (isMedicare) {
            if ((Utility.isNumber(hospitalCharges) && parseFloat(hospitalCharges) >= 0) == false) {
                messages.push('Please specify an amount of zero or higher for the Hospital Donor Nephrectomy.');
                results.validHospitalCharges = false;
            }

            if (hospitalChargesFile.length == 0 && results.validHospitalCharges && parseFloat(hospitalCharges) > 0) {
                messages.push('Please upload a file for the Hospital Donor Nephrectomy.');
                results.validHospitalChargesFile = false;
            }
        }

        if (isMedicaid) {
            if ((Utility.isNumber(hospitalCharges) && parseFloat(hospitalCharges) >= 0) == false) {
                messages.push('Please specify an amount of zero or higher for the Hospital Donor Nephrectomy.');
                results.validHospitalCharges = false;
            }

            if (hospitalChargesFile.length == 0 && results.validHospitalCharges && parseFloat(hospitalCharges) > 0) {
                messages.push('Please upload a file for the Hospital Donor Nephrectomy.');
                results.validHospitalChargesFile = false;
            }
        }

        if (isGlobal) {
            if ((Utility.isNumber(asaUnitCount) && parseFloat(asaUnitCount) >= 0) == false) {
                messages.push('Please specify a value of zero or higher for the ASA units.');
                results.validAsaUnitCount = false;
            }

            if (anesthesiaChargesFile.length == 0 && results.validAsaUnitCount && parseFloat(asaUnitCount) > 0) {
                messages.push('Please upload a file for the Anesthesia.');
                results.validAnesthesiaChargesFile = false;
            }

            if ((Utility.isNumber(hospitalCharges) && parseFloat(hospitalCharges) >= 0) == false) {
                messages.push('Please specify an amount of zero or higher for the Hospital Donor Nephrectomy.');
                results.validHospitalCharges = false;
            }

            if (hospitalChargesFile.length == 0 && results.validHospitalCharges && parseFloat(hospitalCharges) > 0) {
                messages.push('Please upload a file for the Hospital Donor Nephrectomy.');
                results.validHospitalChargesFile = false;
            }
        }

        if (!isSbs && !isMedicare && !isMedicaid && !isGlobal) {
            if ((Utility.isNumber(hospitalCharges) && parseFloat(hospitalCharges) >= 0) == false) {
                messages.push('Please specify an amount of zero or higher for the Hospital Donor Nephrectomy.');
                results.validHospitalCharges = false;
            }

            if (hospitalChargesFile.length == 0 && results.validHospitalCharges && parseFloat(hospitalCharges) > 0) {
                messages.push('Please upload a file for the Hospital Donor Nephrectomy.');
                results.validHospitalChargesFile = false;
            }
        }

        if (messages.length > 0) {
            results.message =
                <div>
                    <div>The following issues were found:</div>
                    <div style={{ marginTop: 5 }}>
                        {messages.map((message, index) =>
                            <div key={index}>&middot; {message}</div>
                        )}
                    </div>
                </div>;
        }

        return results;
    }

    save = (accept: boolean) => {
        let {
            validHospitalCharges, validHospitalChargesFile,
            validAnesthesiaCharges, validAnesthesiaChargesFile,
            validPhysicianCharges, validPhysicianChargesFile,
            validAsaUnitCount, message } = this.validate();

        if (message == null) {
            let billableNephrectomy = this.props.billing.details.billableNephrectomy;
            let { donor, recipient } = billableNephrectomy;

            let hospitalW9File = this.state.hospitalFiles.find(x => x.type == 'hospital-w9-form');
            let physicianChargesFile = this.state.hospitalFiles.find(x => x.type == 'physician-charges');
            let hospitalChargesFile = this.state.hospitalFiles.find(x => x.type == 'hospital-charges');
            let anesthesiaChargesFile = this.state.hospitalFiles.find(x => x.type == 'anesthesia-charges');

            var details: ISaveBillingDetails = {
                donorCenterId: donor.center.id,
                donorId: donor.id,
                centerRoutingNumber: this.state.centerRoutingNumber,
                centerAccountNumber: this.state.centerAccountNumber,
                centerW9File: hospitalW9File != null ? hospitalW9File.file : null,
                recipientId: recipient.id,
                physicianCharges: parseFloat(this.state.physicianCharges),
                physicianChargesFile: physicianChargesFile && physicianChargesFile.file ? physicianChargesFile.file : null,
                anesthesiaCharges: parseFloat(this.state.anesthesiaCharges),
                anesthesiaChargesFile: anesthesiaChargesFile && anesthesiaChargesFile.file ? anesthesiaChargesFile.file : null,
                hospitalCharges: parseFloat(this.state.hospitalCharges),
                hospitalChargesFile: hospitalChargesFile && hospitalChargesFile.file ? hospitalChargesFile.file : null,
                asaUnitCount: parseFloat(this.state.asaUnitCount),
                isRecipientCenter: false,
                accept
            }

            this.props.showLoading('Saving the invoice, one moment please...').then(() => {
                this.props.saveDetails(details).then((response: IBillingDetailResponse) => {
                    this.props.hideLoading();

                    let message = response.data.success ? 'Invoice started and saved.' : 'We encountered an error while attempting to save the invoice.'

                    if (accept && response.data.success) {
                        message = 'This invoice has been saved and marked as accepted by the recipient center.'
                    }

                    this.setState({
                        alert: {
                            show: true,
                            success: response.data.success,
                            message: message
                        }
                    });
                });
            });
        } else {
            this.setState({
                validHospitalCharges,
                validHospitalChargesFile,
                validAnesthesiaCharges,
                validAnesthesiaChargesFile,
                validPhysicianCharges,
                validPhysicianChargesFile,
                validAsaUnitCount,
                alert: {
                    show: true,
                    success: false,
                    message
                },
                dirty: true
            }, () => {
                window.scroll(0, 0);
            });
        }
    }

    render = () => {
        const details = this.props.billing.details;

        if (details == null)
            return null;

        const { donor, recipient, chainId, clusterNumber, positionNumber, physicianChargeStatusId, anesthesiaChargeStatusId, hospitalChargeStatusId } = details.billableNephrectomy;
        const hospitalChargeComments = details.billableNephrectomy != null ? details.billableNephrectomy.hospitalChargeComments : '';
        const anesthesiaChargeComments = details.billableNephrectomy != null ? details.billableNephrectomy.anesthesiaChargeComments : '';
        const recipientCenter = details.billableNephrectomy != null ? details.billableNephrectomy.recipient.center.alias : '';
        const disableSubmitButton = false;

        const wasDisputed = physicianChargeStatusId == ChargeStatus.Disputed || anesthesiaChargeStatusId == ChargeStatus.Disputed || hospitalChargeStatusId == ChargeStatus.Disputed;

        const isAdmin = this.props.app.user.group.isSuper;

        const physicianDonorNephrectomyCharges = donor.rdnCenter == null ? donor.center.physicianDonorNephrectomyCharges : donor.rdnCenter.physicianDonorNephrectomyCharges;

        return (
            <>
                <div style={{ marginBottom: 10, borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderBottomLeftRadius: 4, borderBottomRightRadius: 4, padding: '10px 5px 0px 5px' }}>
                    <NavSubWrapper verticalAlign='center' minTabWidth='85' fontSize='12px'>
                        <NavDonorCenter
                            section={'VIEW-EDIT'}
                            tabText={this.props.tabText}
                            centerId={this.props.centerId}
                            centerAlias={this.props.centerAlias}
                        />
                    </NavSubWrapper>

                    <div className='container'>
                        {this.state.alert.show && (
                            <div className='row' style={{ marginTop: 0 }}>
                                <div className='col-xs-12'>
                                    <Alert bsStyle={this.state.alert.success ? 'success' : 'danger'}>{this.state.alert.message}</Alert>
                                </div>
                            </div>
                        )}

                        {wasDisputed && (
                            <div className='row' style={{ marginTop: 0 }}>
                                <div className='col-xs-12'>
                                    <Alert>
                                        This was invoice was disputed by {recipientCenter} on {details.dateDisputedByRecipientCenter}
                                    </Alert>
                                </div>
                            </div>
                        )}

                        {isAdmin && (
                            <div className='row' style={{ marginTop: 0 }}>
                                <div className='col-xs-12'>
                                    <Alert bsStyle='warning'>
                                        You are currently logged in as an Admin; you can change any items applicable available based on the insurance.
                                    </Alert>
                                </div>
                            </div>
                        )}



                        <div className='row row-margined'>
                            <div className='col-xs-12'>
                                <SwapInfo
                                    chainId={chainId}
                                    clusterNumber={clusterNumber}
                                    positionNumber={positionNumber}
                                    transplantDate={recipient.transplantDate}
                                    isSbs={details.isSbs}
                                    isGlobal={details.isGlobal}
                                    recipient={recipient}
                                    donor={donor}
                                />

                                <div className='row'>
                                    <div className='col-xs-12'>
                                        <Panel>
                                            <Panel.Heading style={{ fontSize: 16 }}>
                                                NKR Terms and Conditions
                                            </Panel.Heading>

                                            <Panel.Body>
                                                {details.isSbs ?
                                                    <StreamlinedBillingService
                                                        physicianCharges={details.physicianCharges}
                                                        anesthesiaCharges={details.anesthesiaCharges}
                                                        hospitalCharges={details.hospitalCharges}
                                                    />
                                                    :
                                                    details.isMedicare ?
                                                        <Medicare
                                                            medicareNumber={this.props.billing.details.billableNephrectomy.recipient.insurance.medicareNumber}
                                                            hospitalCharges={this.state.hospitalCharges}
                                                            validHospitalCharges={this.state.validHospitalCharges}
                                                            hospitalChargesFile={this.props.billing.details.hospitalChargesFile}
                                                            selectedHospitalChargesFile={this.state.hospitalFiles.find(x => x.type == FileType.HospitalCharges)}
                                                            validHospitalChargesFile={this.state.validHospitalChargesFile}
                                                            hospitalChargeStatus={hospitalChargeStatusId}
                                                            hospitalChargeComments={hospitalChargeComments}
                                                            recipientCenter={recipientCenter}
                                                            disputedBy={details.disputedBy}
                                                            viewOnly={details.isBillSent && this.props.app.user.group.isSuper == false}
                                                            dirty={this.state.dirty}
                                                            onHospitalChargesChange={this.handleHospitalChargesChange}
                                                            onHospicalChargesFileChange={this.handleHospitalChargesFileChange}
                                                        />
                                                        :
                                                        details.isMedicaid ?
                                                            <Medicaid
                                                                hospitalCharges={this.state.hospitalCharges}
                                                                validHospitalCharges={this.state.validHospitalCharges}
                                                                hospitalChargesFile={this.props.billing.details.hospitalChargesFile}
                                                                selectedHospitalChargesFile={this.state.hospitalFiles.find(x => x.type == FileType.HospitalCharges)}
                                                                validHospitalChargesFile={this.state.validHospitalChargesFile}
                                                                hospitalChargeStatus={hospitalChargeStatusId}
                                                                hospitalChargeComments={hospitalChargeComments}
                                                                recipientCenter={recipientCenter}
                                                                disputedBy={details.disputedBy}
                                                                viewOnly={details.isBillSent && this.props.app.user.group.isSuper == false}
                                                                dirty={this.state.dirty}
                                                                onHospitalChargesChange={this.handleHospitalChargesChange}
                                                                onHospicalChargesFileChange={this.handleHospitalChargesFileChange}
                                                            />
                                                            :
                                                            details.isGlobal ?
                                                                <Global
                                                                    centerId={this.props.centerId}
                                                                    asaUnitPrice={this.props.billing.details.asaUnitPrice}
                                                                    asaUnitCount={this.state.asaUnitCount}
                                                                    validAsaUnitCount={this.state.validAsaUnitCount}
                                                                    physicianCharges={physicianDonorNephrectomyCharges}
                                                                    hospitalCharges={this.state.hospitalCharges}
                                                                    validHospitalCharges={this.state.validHospitalCharges}
                                                                    hospitalChargesFile={this.props.billing.details.hospitalChargesFile}
                                                                    selectedHospitalChargesFile={this.state.hospitalFiles.find(x => x.type == FileType.HospitalCharges)}
                                                                    validHospitalChargesFile={this.state.validHospitalChargesFile}
                                                                    hospitalChargeStatus={hospitalChargeStatusId}
                                                                    hospitalChargeComments={hospitalChargeComments}
                                                                    anesthesiaChargesFile={this.props.billing.details.anesthesiaChargesFile}
                                                                    selectedAnesthesiaChargesFile={this.state.hospitalFiles.find(x => x.type == FileType.AnesthesiaCharges)}
                                                                    validAnesthesiaChargesFile={this.state.validAnesthesiaChargesFile}
                                                                    anesthesiaChargeStatus={anesthesiaChargeStatusId}
                                                                    anesthesiaChargeComments={anesthesiaChargeComments}
                                                                    recipientCenter={recipientCenter}
                                                                    disputedBy={details.disputedBy}
                                                                    dirty={this.state.dirty}
                                                                    viewOnly={details.isBillSent && this.props.app.user.group.isSuper == false}
                                                                    onHospitalChargesChange={this.handleHospitalChargesChange}
                                                                    onHospicalChargesFileChange={this.handleHospitalChargesFileChange}
                                                                    onAnesthesiaChargesFileChange={this.handleHospitalChargesFileChange}
                                                                    onAsaUnitCountChange={this.handleAsaUnitCountChange}
                                                                    onEditMedicareParticipating={this.handleEditMedicareParticipating}
                                                                />
                                                                :
                                                                <NonGlobal
                                                                    hospitalCharges={this.state.hospitalCharges}
                                                                    validHospitalCharges={this.state.validHospitalCharges}
                                                                    hospitalChargesFile={this.props.billing.details.hospitalChargesFile}
                                                                    selectedHospitalChargesFile={this.state.hospitalFiles.find(x => x.type == FileType.HospitalCharges)}
                                                                    validHospitalChargesFile={this.state.validHospitalChargesFile}
                                                                    hospitalChargeStatus={hospitalChargeStatusId}
                                                                    hospitalChargeComments={hospitalChargeComments}
                                                                    recipientCenter={recipient.center.alias}
                                                                    recipientAlias={recipient.alias}
                                                                    disputedBy={details.disputedBy}
                                                                    insurance={recipient.insurance}
                                                                    viewOnly={details.isBillSent && this.props.app.user.group.isSuper == false}
                                                                    dirty={this.state.dirty}
                                                                    onHospitalChargesChange={this.handleHospitalChargesChange}
                                                                    onHospicalChargesFileChange={this.handleHospitalChargesFileChange}
                                                                />
                                                }
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                </div>

                                <DirectDepositInformation
                                    details={this.props.billing.details}
                                    centerRoutingNumber={this.state.centerRoutingNumber}
                                    centerAccountNumber={this.state.centerAccountNumber}
                                    hospitalW9Form={this.state.hospitalFiles.find(x => x.type == FileType.HospitalW9Form)}
                                    wasSubmittedByRecipientCenter={false}
                                    isAdmin={isAdmin}
                                    onChangeCenterRoutingNumber={this.handleCenterRoutingNumberChange}
                                    onChangeCenterAccountNumber={this.handleCenterAccountNumberChange}
                                    onHospitalW9Form={this.handleHospitalW9Form}
                                />

                                {(!details.isBillSent || isAdmin) && (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        {isAdmin && (
                                            <button className='btn btn-warning btn-lg' style={{ marginRight: 20 }} onClick={this.handleSubmitAccept} disabled={disableSubmitButton}>SUBMIT INVOICE &amp; ACCEPT</button>
                                        )}

                                        <button className='btn btn-primary btn-lg' onClick={this.handleSave} disabled={disableSubmitButton}>SUBMIT INVOICE</button>
                                        <button className='btn btn-link' onClick={this.handleCancel}>Cancel &amp; Return to the Pending List</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <MedicareParticipating
                    show={this.state.showMedicareParticipating}
                    medicareParticipating={this.props.billing.details.billableNephrectomy.donor.center.physicianDonorNephrectomyCharges.toString()}
                    onSave={this.handleSaveMedicareParticipating}
                    onCancel={this.handleCancelMedicareParticipating}
                />

                <Modal show={this.state.showSubmitAcceptConfirm} backdrop='static' onHide={this.handleSubmitAcceptNo}>
                    <Modal.Header closeButton>
                        <Modal.Title>Submit &amp; Accept Confirmation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want submit and accept as the recipient center?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsStyle='primary' onClick={this.handleSubmitAcceptYes}>Yes</Button>
                        <Button bsStyle='link' onClick={this.handleSubmitAcceptNo}>No</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        billing: appState.billing,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getDetails: (donorId, centerId) => { return dispatch(BillingActionCreators.getDetails(donorId, centerId)); },
        saveDetails: (details) => { return dispatch(BillingActionCreators.saveDetails(details)); },
        saveMedicareParticipating: (centerId, MedicareParticipating) => { return dispatch(BillingActionCreators.saveMedicareParticipating(centerId, MedicareParticipating)); }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(ViewEdit);